import { ApolloClient } from '@apollo/client';
import { VOUCHER_LIST_ITEM_QUERY } from './vouchers.gql';

export interface VoucherListRecord {
	voucherId: string;
	code: string;
	name?: string | null;
	isAdHoc: boolean;
	isCustomerSpecific: boolean;
	discountValue: number;
	maxDiscountAmount?: number | null;
	usageLimit?: number | null;
	currentUsageCount: number;
	isActive: boolean;
	validUntilDate: string;
	voucherDiscountType?: {
		voucherDiscountTypeId: number;
		name?: string | null;
	} | null;
	campaign?: { campaignId: string; name?: string | null } | null;
}

interface VoucherListItemResponse {
	voucher?: VoucherListRecord | null;
}

export interface SelectVoucherAfterSaveOptions {
	voucherId: string;
	client: ApolloClient<any>;
	currentVouchers: VoucherListRecord[];
	onDataUpdate: (updatedVouchers: VoucherListRecord[]) => void;
	onVoucherSelect: (voucher: VoucherListRecord) => void;
}

/**
 * Fetches the latest voucher data from the server and updates the list,
 * then selects and highlights the voucher in the grid and details panel.
 * 
 * @param options Configuration options for selecting the voucher
 * @returns Promise that resolves when the operation is complete
 */
export async function selectVoucherAfterSave(
	options: SelectVoucherAfterSaveOptions
): Promise<void> {
	const { voucherId, client, currentVouchers, onDataUpdate, onVoucherSelect } = options;

	try {
		// Step 1: Fetch the latest voucher data from the server
		const { data } = await client.query<VoucherListItemResponse>({
			query: VOUCHER_LIST_ITEM_QUERY,
			variables: { voucherId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedVoucher = data?.voucher;

		if (!fetchedVoucher) {
			console.warn(`Voucher with ID ${voucherId} not found after save`);
			return;
		}

		// Step 2: Check if voucher exists in current list
		const existingVoucherIndex = currentVouchers.findIndex(
			v => v.voucherId === voucherId
		);

		let updatedVouchers: VoucherListRecord[];

		if (existingVoucherIndex >= 0) {
			// Voucher exists - replace with updated data
			updatedVouchers = [...currentVouchers];
			updatedVouchers[existingVoucherIndex] = fetchedVoucher;
		} else {
			// New voucher - append to the list
			updatedVouchers = [...currentVouchers, fetchedVoucher];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedVouchers);

		// Step 4: Select the voucher (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onVoucherSelect(fetchedVoucher);
		}, 50);
	} catch (error) {
		console.error('Error selecting voucher after save:', error);
		throw error;
	}
}